import { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, updateDoc, setDoc, deleteDoc, serverTimestamp, query, where } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { parseDataRow } from './utils';
import { getTodayDate } from './utils';
import { MAX_ROWS, PROPAGATE_FIELDS } from './constants';
import { toast } from 'react-toastify';
import { rateLimiter, formatTimeUntilReset } from '../../utils/rateLimiter';
import { listSeasons } from '../../utils/seasonApi';
import { getCurrentSeason } from '../../data/seasons';
import { clearPlayerStatsSeasonCache } from '../../utils/playerStatsSessionCache';
import { getSeasonConfig } from '../../utils/matchTypeCatalog';

const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
const LAST_MODE_KEY = 'ps_last_mode';
const lastMapKey = (mode) => `ps_last_map_${mode}`;
const buildFlatEntryId = (matchId, entryId) => `${matchId}__${entryId}`;

export const useAddPlayerStatsForm = () => {
  const [searchParams] = useSearchParams();
  const teamIdFromQuery = searchParams.get('teamId');
  const entryIdFromQuery = searchParams.get('entryId');
  const seasonIdFromQuery = searchParams.get('seasonId');
  const matchIdFromQuery = searchParams.get('matchId');
  
  const [userProfile, setUserProfile] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [activeMode, setActiveMode] = useState(() => {
    return localStorage.getItem(LAST_MODE_KEY) || 'HP';
  }); // Default to last-used or Hardpoint
  const [structuredData, setStructuredData] = useState([]);
  const [activePlayers, setActivePlayers] = useState([]);
  const [playerImages, setPlayerImages] = useState({});
  const [matchTypes, setMatchTypes] = useState([]);
  const [matchTypeCatalog, setMatchTypeCatalog] = useState([]);
  const [vsTeams, setVsTeams] = useState([]);
  const [regions, setRegions] = useState([]);
  const [seasonDocId, setSeasonDocId] = useState(null); // target season doc id
  const [seasonTeamId, setSeasonTeamId] = useState(null);
  const [seasonTeamName, setSeasonTeamName] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [isEditMatch, setIsEditMatch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef({});

  const getPlayerStatsBasePath = useCallback((seasonIdOverride = null) => {
    const seasonPath = seasonIdOverride ? `/seasons/${seasonIdOverride}` : '';
    return `teams/${teamId}${seasonPath}/database/playerStats`;
  }, [teamId]);

  const buildFlatEntryPayload = useCallback((parsedRow, { matchId, entryId }) => ({
    ...parsedRow,
    Mode: activeMode,
    ModeID: matchId,
    matchId,
    legacyEntryId: entryId,
    Team: seasonTeamName || userProfile?.teamName || teamId || '',
    TeamId: seasonTeamId || teamId,
    Region: parsedRow.Region || '',
    VS: parsedRow.VS || '',
    MatchType: parsedRow.MatchType || '',
    Date: parsedRow.Date || getTodayDate(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }), [activeMode, seasonTeamId, seasonTeamName, teamId, userProfile]);

  // Fetch user and team info
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) return;

      const userRef = doc(db, `users/${user.uid}`);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserProfile(data);

        const finalTeamId = teamIdFromQuery || data.teamId;
        if (finalTeamId) {
          setTeamId(finalTeamId);
        }
      }
    });

    return unsubscribe;
  }, [teamIdFromQuery]);

  // Fetch team configuration and target season for new saves.
  useEffect(() => {
    const fetchConfig = async () => {
      if (!teamId) return;
      
      try {
        const targetYear = getCurrentSeason();
        const seasons = await listSeasons(teamId);
        const seasonById = seasons.find(s => String(s.id) === targetYear);
        const seasonByYear = seasons.find(s => String(s.year || s.id) === targetYear);
        const chosenSeason = seasonById || seasonByYear || seasons[0] || null;
        const shouldUseQuerySeason = Boolean(seasonIdFromQuery && (entryIdFromQuery || matchIdFromQuery));
        const chosenSeasonId = shouldUseQuerySeason
          ? String(seasonIdFromQuery)
          : (chosenSeason ? String(chosenSeason.id) : targetYear);
        setSeasonDocId(chosenSeasonId);
        // load season identity for display
        const seasonSnap = await getDoc(doc(db, `teams/${teamId}/seasons/${chosenSeasonId}`));
        if (seasonSnap.exists()) {
          const s = seasonSnap.data();
          setSeasonTeamId(s.externalTeamId || s.teamId || teamId);
          setSeasonTeamName(s.displayName || s.teamName || s.externalTeamId || '');
        }

        const configRef = doc(db, `teams/${teamId}/configurations/stats`);
        const configSnap = await getDoc(configRef);
        
        if (configSnap.exists()) {
          const config = configSnap.data();
          const players = (config.players || [])
            .filter(p => p.isActive !== false)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          setActivePlayers(players.map(p => p.ign || p.name || ''));
          const images = {};
          players.forEach(p => {
            const key = p.ign || p.name;
            if (key) images[key] = p.imageUrl || '';
          });
          setPlayerImages(images);
          
          // Load config for the resolved write season year.
          const seasonRow = seasons.find((s) => String(s.id) === String(chosenSeasonId));
          const currentSeason = seasonRow
            ? String(seasonRow.year || seasonRow.id)
            : String(chosenSeasonId || targetYear);
          const seasonConfig = getSeasonConfig(config, currentSeason);
          setMatchTypes(seasonConfig.matchTypes || []);
          setMatchTypeCatalog(seasonConfig.matchTypeCatalog || []);
          
          const rawTeams = seasonConfig.vsTeams || [];
          const normalizedTeams = rawTeams.map(t => ({
            name: t.name.trim().toUpperCase(),
            region: t.region.trim().toUpperCase()
          }));
          setVsTeams(normalizedTeams);
          setRegions([...new Set(normalizedTeams.map(t => t.region))].sort());
        }
      } catch (err) {
        console.error('Failed to fetch config:', err);
        toast.error('Failed to load team configuration');
      }
    };

    fetchConfig();
  }, [teamId, seasonIdFromQuery, entryIdFromQuery, matchIdFromQuery]);

  // Prefill when editing an existing entry
  useEffect(() => {
    const loadExisting = async () => {
      if (!teamId) return;

      // Edit by matchId (bulk)
      if (matchIdFromQuery) {
        const seasonPath = seasonIdFromQuery ? `/seasons/${seasonIdFromQuery}` : '';
        const matchRef = doc(db, `teams/${teamId}${seasonPath}/database/playerStats/matches/${matchIdFromQuery}`);
        const matchSnap = await getDoc(matchRef);
        if (!matchSnap.exists()) {
          toast.error('Could not load match for editing');
          return;
        }
        const entriesSnap = await getDocs(collection(matchRef, 'entries'));
        if (entriesSnap.empty) {
          toast.error('No entries found for this match');
          return;
        }
        const rows = entriesSnap.docs.map(d => {
          const data = d.data();
          return {
            _rowId: generateUniqueId(),
            Player: data.Player || '',
            Score: data.Score || '',
            K: data.K || '',
            D: data.D || '',
            A: data.A || '',
            Impact: data.Impact || '',
            Map: data.Map || '',
            Operator: data.Operator || '',
            MatchType: data.MatchType || '',
            MatchTypeId: data.MatchTypeId || '',
            MatchCategory: data.MatchCategory || '',
            MatchTypeRegion: data.MatchTypeRegion || '',
            Region: data.Region || '',
            VS: data.VS || '',
            Date: data.Date || getTodayDate(),
            WinLose: data.WinLose || '',
            PF: data.PF || '',
            PA: data.PA || '',
            HillTime: data.HillTime || '',
            TotalDamage: data.TotalDamage || '',
            CaptureKills: data.CaptureKills || '',
            DistanceMoved: data.DistanceMoved || '',
            OperatorValue: data.OperatorValue || '',
            ADR: data.ADR || '',
            FirstBloods: data.FirstBloods || '',
            Clutches1v1: data.Clutches1v1 || '',
            Damage: data.Damage || ''
          };
        });
        setIsEdit(true);
        setIsEditMatch(true);
        if (matchSnap.data()?.Mode) setActiveMode(matchSnap.data().Mode);
        // Parent syncs MatchType/Region/VS/Date from first row via structuredData.
        setStructuredData(rows);
        return;
      }

      // Edit single entry
      if (entryIdFromQuery) {
        const seasonPath = seasonIdFromQuery ? `/seasons/${seasonIdFromQuery}` : '';
        const ref = doc(db, `teams/${teamId}${seasonPath}/database/playerStats/entries/${entryIdFromQuery}`);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          toast.error('Could not load player stat for editing');
          return;
        }
        const data = snap.data() || {};
        setIsEdit(true);
        if (data.Mode) setActiveMode(data.Mode);
        const row = {
          _rowId: generateUniqueId(),
          Player: data.Player || '',
          Score: data.Score || '',
          K: data.K || '',
          D: data.D || '',
          A: data.A || '',
          Impact: data.Impact || '',
          Map: data.Map || '',
          Operator: data.Operator || '',
          MatchType: data.MatchType || '',
          MatchTypeId: data.MatchTypeId || '',
          MatchCategory: data.MatchCategory || '',
          MatchTypeRegion: data.MatchTypeRegion || '',
          Region: data.Region || '',
          VS: data.VS || '',
          Date: data.Date || getTodayDate(),
          WinLose: data.WinLose || '',
          PF: data.PF || '',
          PA: data.PA || '',
          HillTime: data.HillTime || '',
          TotalDamage: data.TotalDamage || '',
          CaptureKills: data.CaptureKills || '',
          DistanceMoved: data.DistanceMoved || '',
          OperatorValue: data.OperatorValue || '',
          ADR: data.ADR || '',
          FirstBloods: data.FirstBloods || '',
          Clutches1v1: data.Clutches1v1 || '',
          Damage: data.Damage || ''
        };
        setStructuredData([row]);
      }
    };
    loadExisting();
  }, [teamId, entryIdFromQuery, seasonIdFromQuery, matchIdFromQuery]);

  // Initialize structured data when mode changes
  useEffect(() => {
    // Editing flows load existing rows from Firestore; don't overwrite them.
    if (isEdit) return;

    if (!activeMode || !activePlayers.length) {
      setStructuredData([]);
      return;
    }

    const initialData = Array(MAX_ROWS).fill(null).map(() => ({
      _rowId: generateUniqueId(),
      Player: '',
      Score: '',
      K: '',
      D: '',
      A: '',
      Impact: '',
      Map: '',
      Operator: '',
      MatchType: '',
      MatchTypeId: '',
      MatchCategory: '',
      MatchTypeRegion: '',
      Region: '',
      VS: '',
      Date: getTodayDate(),
      WinLose: '',
      PF: '',
      PA: '',
      ...(activeMode === 'HP' && { HillTime: '', TotalDamage: '', CaptureKills: '', DistanceMoved: '', OperatorValue: '' }),
      ...(activeMode === 'SND' && { ADR: '', FirstBloods: '', Clutches1v1: '' }),
      ...(activeMode === 'CTL' && { Damage: '', CaptureKills: '', OperatorValue: '' })
    }));

    setStructuredData(initialData);
  }, [activeMode, activePlayers, isEdit]);

  // Prefill last-used map per mode when switching modes
  useEffect(() => {
    const lastMap = localStorage.getItem(lastMapKey(activeMode));
    if (!lastMap || !structuredData.length) return;
    const first = structuredData[0];
    if (first?._rowId) {
      // update map for all rows via updateCell propagate logic in calling component
      // we only update here if map is empty to avoid overwriting user input
      if (!first.Map) {
        // expose helper: we'll just mutate here using setStructuredData
        setStructuredData(prev =>
          prev.map((row, idx) =>
            idx === 0 ? { ...row, Map: lastMap } : row
          )
        );
      }
    }
  }, [activeMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateCell = useCallback((rowId, fieldKey, value) => {
    setStructuredData(prev => {
      const firstRow = prev[0];
      const propagateFields = PROPAGATE_FIELDS;
      
      // If updating row 1 and it's a propagate field, update all rows
      if (firstRow && firstRow._rowId === rowId && propagateFields.includes(fieldKey)) {
        return prev.map(row => ({ ...row, [fieldKey]: value }));
      }
      
      // Otherwise, just update the specific row
      return prev.map(row => 
        row._rowId === rowId ? { ...row, [fieldKey]: value } : row
      );
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!teamId || !activeMode || isSubmitting) return;

    const validRows = structuredData.filter(row => row.Player && row.Score);
    if (validRows.length === 0) {
      toast.error('Please fill at least one row with Player and Score');
      return;
    }
    if (!isEdit && validRows.length > 5) {
      toast.error('You can submit at most 5 players at a time.');
      return;
    }

    if (!isEdit) {
      // Check rate limit
      const check = rateLimiter.checkLimit('SUBMIT_PLAYER_STATS');
      if (!check.allowed) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('SUBMIT_PLAYER_STATS'));
        toast.error(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
        return;
      }

      if (!rateLimiter.record('SUBMIT_PLAYER_STATS')) {
        const timeUntilReset = formatTimeUntilReset(rateLimiter.getTimeUntilReset('SUBMIT_PLAYER_STATS'));
        toast.error(`Rate limit exceeded. Please try again in ${timeUntilReset}.`);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      if (isEditMatch && matchIdFromQuery) {
        // Overwrite entries within the match group
        const seasonPath = seasonIdFromQuery ? `/seasons/${seasonIdFromQuery}` : (seasonDocId ? `/seasons/${seasonDocId}` : '');
        const matchPath = `teams/${teamId}${seasonPath}/database/playerStats/matches/${matchIdFromQuery}`;
        const flatEntriesPath = `${getPlayerStatsBasePath(seasonIdFromQuery || seasonDocId)}/entries`;

        // Delete existing entries
        const existing = await getDocs(collection(db, `${matchPath}/entries`));
        await Promise.all(existing.docs.map(d => deleteDoc(d.ref)));
        const existingFlat = await getDocs(query(collection(db, flatEntriesPath), where('matchId', '==', matchIdFromQuery)));
        await Promise.all(existingFlat.docs.map(d => deleteDoc(d.ref)));

        // Write match meta
        const firstRow = validRows[0];
        await setDoc(doc(db, matchPath), {
          Mode: activeMode,
          ModeID: matchIdFromQuery,
          Date: firstRow?.Date || getTodayDate(),
          MatchType: firstRow?.MatchType || '',
          MatchTypeId: firstRow?.MatchTypeId || '',
          MatchCategory: firstRow?.MatchCategory || '',
          MatchTypeRegion: firstRow?.MatchTypeRegion || '',
          Region: firstRow?.Region || '',
          VS: firstRow?.VS || '',
          Map: firstRow?.Map || '',
          WinLose: firstRow?.WinLose || '',
          PF: Number(firstRow?.PF || 0),
          PA: Number(firstRow?.PA || 0),
          updatedAt: serverTimestamp(),
        }, { merge: true });

        // Write entries under match
        for (const row of validRows) {
          const parsedRow = parseDataRow(row);
          const entryRef = doc(collection(db, `${matchPath}/entries`));
          const basePayload = {
            ...parsedRow,
            Mode: activeMode,
            ModeID: matchIdFromQuery,
            Team: seasonTeamName || userProfile?.teamName || teamId || '',
            TeamId: seasonTeamId || teamId,
            Date: parsedRow.Date || getTodayDate(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          await setDoc(entryRef, basePayload);
          await setDoc(
            doc(db, `${flatEntriesPath}/${buildFlatEntryId(matchIdFromQuery, entryRef.id)}`),
            buildFlatEntryPayload(parsedRow, { matchId: matchIdFromQuery, entryId: entryRef.id }),
            { merge: true }
          );
        }

        clearPlayerStatsSeasonCache(teamId, seasonIdFromQuery || seasonDocId);
        toast.success(`✅ Match ${matchIdFromQuery} updated`);
      } else if (isEdit && entryIdFromQuery) {
        const row = parseDataRow(validRows[0]);
        const basePath = seasonIdFromQuery
          ? `teams/${teamId}/seasons/${seasonIdFromQuery}/database/playerStats/entries/${entryIdFromQuery}`
          : `teams/${teamId}/database/playerStats/entries/${entryIdFromQuery}`;
        const docRef = doc(db, basePath);
        await updateDoc(docRef, {
          ...row,
          Mode: activeMode,
          ModeID: row.ModeID || `mode_${activeMode.toLowerCase()}`,
          Team: seasonTeamName || userProfile?.teamName || teamId || '',
          TeamId: seasonTeamId || teamId,
          Region: row.Region || validRows[0].Region || '',
          VS: row.VS || validRows[0].VS || '',
          MatchType: row.MatchType || validRows[0].MatchType || '',
          MatchTypeId: row.MatchTypeId || validRows[0].MatchTypeId || '',
          MatchCategory: row.MatchCategory || validRows[0].MatchCategory || '',
          MatchTypeRegion: row.MatchTypeRegion || validRows[0].MatchTypeRegion || '',
          Date: row.Date || getTodayDate(),
          updatedAt: serverTimestamp(),
        });
        clearPlayerStatsSeasonCache(teamId, seasonIdFromQuery || seasonDocId);
        toast.success('✅ Player stat updated');
      } else {
        const modeId = `mode_${activeMode.toLowerCase()}_${Date.now()}`;
        const firstRowDefaults = structuredData[0] || {};
        const flatEntriesPath = `${getPlayerStatsBasePath(seasonDocId)}/entries`;

        // Also store grouped under matches/{modeId}/entries for this game
        const matchRoot = seasonDocId
          ? `teams/${teamId}/seasons/${seasonDocId}/database/playerStats/matches/${modeId}`
          : `teams/${teamId}/database/playerStats/matches/${modeId}`;
        await setDoc(doc(db, matchRoot), {
          Mode: activeMode,
          ModeID: modeId,
          Date: validRows[0]?.Date || getTodayDate(),
          MatchType: validRows[0]?.MatchType || firstRowDefaults.MatchType || '',
          MatchTypeId: validRows[0]?.MatchTypeId || firstRowDefaults.MatchTypeId || '',
          MatchCategory: validRows[0]?.MatchCategory || firstRowDefaults.MatchCategory || '',
          MatchTypeRegion: validRows[0]?.MatchTypeRegion || firstRowDefaults.MatchTypeRegion || '',
          Region: validRows[0]?.Region || firstRowDefaults.Region || '',
          VS: validRows[0]?.VS || firstRowDefaults.VS || '',
          Map: validRows[0]?.Map || firstRowDefaults.Map || '',
          WinLose: validRows[0]?.WinLose || firstRowDefaults.WinLose || '',
          PF: Number(validRows[0]?.PF || firstRowDefaults.PF || 0),
          PA: Number(validRows[0]?.PA || firstRowDefaults.PA || 0),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        for (const row of validRows) {
          const parsedRow = parseDataRow(row);
          const entryRef = doc(collection(db, `${matchRoot}/entries`));
          const basePayload = {
            ...parsedRow,
            Mode: activeMode,
            ModeID: modeId,
            Team: seasonTeamName || userProfile?.teamName || teamId || '',
            TeamId: seasonTeamId || teamId,
            Date: parsedRow.Date || getTodayDate(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          await setDoc(entryRef, basePayload);
          await setDoc(
            doc(db, `${flatEntriesPath}/${buildFlatEntryId(modeId, entryRef.id)}`),
            buildFlatEntryPayload(parsedRow, { matchId: modeId, entryId: entryRef.id }),
            { merge: true }
          );
        }

        // persist last selections
        localStorage.setItem(LAST_MODE_KEY, activeMode);
        const firstRow = validRows[0];
        if (firstRow?.Map) {
          localStorage.setItem(lastMapKey(activeMode), firstRow.Map);
        }
        clearPlayerStatsSeasonCache(teamId, seasonDocId);
        toast.success(`✅ Successfully submitted ${validRows.length} player stat(s)`);
        
        // Reset form
        setStructuredData(prev => prev.map(row => ({
          ...row,
          Player: '',
          Score: '',
          K: '',
          D: '',
          A: '',
          Impact: ''
        })));
      }

    } catch (err) {
      console.error('Submission error:', err);
      toast.error('❌ Failed to submit player stats');
    } finally {
      setIsSubmitting(false);
    }
  }, [teamId, activeMode, structuredData, userProfile, isSubmitting, seasonDocId, isEdit, isEditMatch, entryIdFromQuery, seasonIdFromQuery, matchIdFromQuery, seasonTeamId, seasonTeamName, getPlayerStatsBasePath, buildFlatEntryPayload]);

  return {
    teamId,
    userProfile,
    activeMode,
    setActiveMode,
    structuredData,
    setStructuredData,
    activePlayers,
    playerImages,
    matchTypes,
    matchTypeCatalog,
    vsTeams,
    regions,
    isSubmitting,
    isEdit,
    inputRefs,
    updateCell,
    handleSubmit
  };
};
